import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router-dom'
import { Press } from '@dorixona/ui'
import { formatPhone } from '@dorixona/utils'
import { BackButton } from '../../ui/BackButton'
import { Icon } from '../../ui/Icon'
import { useSession } from '../../app/session'
import { useSupportPhone } from '../../app/settings'
import { useCards } from '../checkout/cards'
import { useAddresses } from './api'
import { cardName } from './cardName'
import { LinkRow } from './LinkRow'
import { SettingsPanel } from './SettingsPanel'
import { profileView } from './model'
import { env } from '../../config/env'
import { useFavorites } from '../favorites'
import { useStores } from '../stores/api'

/**
 * Личный кабинет — кто вошёл (карточка с правкой данных) и настройки приложения.
 * Заказы и баллы живут во вкладке нижней панели (`/orders`), аптеки сети —
 * строкой в разделах кабинета.
 */
export function ProfileScreen() {
  const navigate = useNavigate()
  const { t } = useTranslation(['account', 'common', 'catalog', 'stores'])
  const { signOut, customer, isAuthenticated } = useSession()
  const profile = profileView(customer)
  const support = useSupportPhone()
  // Справа в строках — основной адрес и последняя карта: сервер отдаёт их первыми.
  const address = useAddresses(isAuthenticated).data?.[0]
  const card = useCards(isAuthenticated).data?.[0]
  // Без флага запроса нет: `useFavorites` сам выключен, строка не рисуется.
  const favorites = useFavorites().data
  // Аптеки сети ушли из нижней панели (там теперь заказы) — вход сюда, справа
  // их число.
  const stores = useStores().data

  return (
    <section className="screen profile-wrap">
      <div className="mhead">
        <BackButton to="/" />
        <h1>{t('account:title')}</h1>
      </div>

      <div className="acc-col">
        <div className="acc-card acc-id">
          <div className="avatar">{profile.initials}</div>
          <div style={{ minWidth: 0 }}>
            <div className="an">{profile.fullName}</div>
            <div className="ap num">{formatPhone(profile.phone)}</div>
          </div>
          <Press
            squish="icon"
            className="acc-edit"
            aria-label={t('account:edit.title')}
            onPress={() => {
              void navigate('/profile/edit')
            }}
          >
            <Icon name="i-edit" size={20} />
          </Press>
        </div>

        <nav className="acc-sec acc-links" aria-label={t('account:sections')}>
          {env.features.favorites ? (
            <LinkRow
              title={t('catalog:favorites.title')}
              value={
                favorites?.length
                  ? t('catalog:goods', { count: favorites.length })
                  : t('catalog:favorites.none')
              }
              to="/favorites"
            />
          ) : null}
          <LinkRow
            title={t('account:addresses.title')}
            value={address ? address.tag || address.label : t('account:add')}
            to="/profile/addresses"
          />
          <LinkRow
            title={t('account:cards.title')}
            value={card ? cardName(card) : t('account:add')}
            to="/profile/cards"
          />
          <LinkRow
            title={t('stores:title')}
            value={stores?.length ? t('stores:count', { count: stores.length }) : ''}
            to="/stores"
          />
        </nav>

        <h2 className="acc-title">{t('account:tabs.settings')}</h2>
        <SettingsPanel />

        <nav className="acc-card acc-extra" aria-label={t('account:more')}>
          {/* Поддержка — звонком в одно касание: номер справа, вся строка — ссылка. */}
          {support ? (
            <a className="acc-support" href={support.href}>
              <Icon name="i-phone" size={19} />
              {t('account:support')}
              <span className="acc-support__num num">{support.text}</span>
            </a>
          ) : null}
          <Press
            squish="row"
            onPress={() => {
              void navigate('/privacy-policy')
            }}
          >
            <Icon name="i-info" size={19} />
            {t('account:privacy')}
          </Press>
          <Press
            squish="row"
            className="danger"
            onPress={() => {
              signOut()
              void navigate('/')
            }}
          >
            <Icon name="i-out" size={19} />
            {t('common:actions.logout')}
          </Press>
        </nav>
      </div>
    </section>
  )
}
